// Single source of truth for interface (UI) locales.
//
// To add a new interface language:
//   1. Create locales/<code>.json with the same key structure as en.json
//      (verify with `npm run i18n:check`).
//   2. Add one entry to UI_LOCALES below.
//   3. Add one entry to UI_LOCALE_LOADERS below (dynamic import — keeps
//      the language lazy-loaded instead of bloating the initial bundle).
// That's it — the language switcher, main.js, and the locale store all
// derive from this list automatically.
//
// NOTE: This is the list of INTERFACE languages only. It is intentionally
// separate from the question-bank language (see stores/game.js and
// QUESTION_LANGUAGES below), which controls which questions_<lang>.json
// file the backend loads for a game.
export const UI_LOCALES = [
  { code: 'en', native: 'English', short: 'EN' },
  { code: 'de', native: 'Deutsch', short: 'DE' },
  { code: 'es', native: 'Español', short: 'ES' },
  { code: 'fr', native: 'Français', short: 'FR' },
  { code: 'pt', native: 'Português', short: 'PT' },
  { code: 'pt-BR', native: 'Português (Brasil)', short: 'BR' },
]

export const DEFAULT_UI_LOCALE = 'en'

export const UI_LOCALE_CODES = UI_LOCALES.map((l) => l.code)

// Languages available in the question bank on the backend
// (questions_<code>.json). Kept separate from UI_LOCALES on purpose:
// translating the interface does NOT mean the question bank has content
// in that language. See FIX-LANGUAGE-HARDCODE-BUG.md for background.
export const QUESTION_LANGUAGES = [
  { code: 'en', native: 'English' },
  { code: 'es', native: 'Español' },
  { code: 'ru', native: 'Русский' },
]

export const QUESTION_LANGUAGE_CODES = QUESTION_LANGUAGES.map((l) => l.code)

export function isUiLocale(code) {
  return UI_LOCALE_CODES.includes(code)
}

export function isQuestionLanguage(code) {
  return QUESTION_LANGUAGE_CODES.includes(code)
}

// Best-effort mapping from a browser locale (e.g. "pt-BR", "fr-CA", "de")
// to one of our supported UI locale codes.
export function resolveBrowserLocale(browserLocales = []) {
  for (const raw of browserLocales) {
    if (!raw) continue
    const lower = raw.toLowerCase()
    if (lower.startsWith('pt-br')) return 'pt-BR'
    if (lower.startsWith('pt')) return 'pt'
    const base = lower.split('-')[0]
    if (UI_LOCALE_CODES.includes(base)) return base
  }
  return DEFAULT_UI_LOCALE
}

// Eagerly import only the default locale — it's needed synchronously at
// app boot (before we know the user's preference) and acts as the
// ultimate fallback for every other locale.
import en from './en.json'

export const UI_LOCALE_MESSAGES = {
  en,
}

// Every other locale is lazy-loaded on demand (see stores/locale.js).
// Now that marketing pages are fully translated, the combined JSON
// payload is large enough that shipping all 6 locales in the initial
// bundle is wasteful — most visitors only ever need one or two of them.
// Keep this map in sync with UI_LOCALES above: one dynamic import per
// non-default language.
export const UI_LOCALE_LOADERS = {
  de: () => import('./de.json'),
  es: () => import('./es.json'),
  fr: () => import('./fr.json'),
  pt: () => import('./pt.json'),
  'pt-BR': () => import('./pt-BR.json'),
}
